<template>
    <div class="my-connections">
        <div class="connections-header">
            <div class="search-network">
                <div class="bg">
                    <common-icon name="search-gray" size="small" class="header-search-layout"></common-icon>
                    <input
                        v-model="search"
                        type="text"
                        :placeholder="$t('forms.network.fields.search.placeholder')"
                        class="search"
                    />
                </div>

                <div class="search-network-grid">
                    <common-icon
                        v-for="groupTab in groupTabs"
                        :key="groupTab.name"
                        :name="groupTab.icon"
                        size="large"
                        :class="groupTab.iconClass"
                        @click="groupTabChange(groupTab.name)"
                    ></common-icon>
                </div>
            </div>
            <div class="toggle-tabs">
                <button v-for="tab in tabs" :key="tab.name" :class="tab.buttonClass" @click="tabChange(tab.name)">
                    <span class="dot" :class="tab.name === 'physicians' ? 'green' : 'blue'"></span>
                    {{ tab.displayName }}
                </button>
            </div>
        </div>

        <div>
            <common-loader :isProcessing="isLoading"></common-loader>
            <my-connections-list
                v-if="!isLoading && activeTab === 'physicians'"
                ref="physicianConnectionList"
                :connections="filteredPhysicianConnections"
                :totalCount="filteredPhysicianConnections.length"
                :connectionDirection="physicianFilter.connectionDirection"
                :orderBy="physicianFilter.orderBy"
                :isPhysicianTab="showTabPhysicians"
                :viewType="viewType"
                @filter-changed="physicianFilterChanged"
                @load-more="physicianLoadNextPage"
                @reload-users="fetchData(false)"
            ></my-connections-list>
            <my-connections-list
                v-if="!isLoading && activeTab === 'providers'"
                ref="ancillaryConnectionList"
                :connections="filteredAncillaryConnections"
                :totalCount="filteredAncillaryConnections.length"
                :connectionDirection="ancillaryFilter.connectionDirection"
                :orderBy="ancillaryFilter.orderBy"
                :isPhysicianTab="showTabPhysicians"
                :viewType="viewType"
                @filter-changed="ancillaryFilterChanged"
                @load-more="ancillaryLoadNextPage"
                @reload-users="fetchData(false)"
            ></my-connections-list>
        </div>
    </div>
</template>

<script>
    import MyConnectionsList from './MyConnectionsList'
    import UsersAPI from 'api/UsersAPI'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

    export default {
        components: {
            MyConnectionsList,
        },
        data() {
            return {
                pageSize: 0,
                showTabPhysicians: true,
                physicianConnections: [],
                ancillaryConnections: [],
                physicianFilter: {
                    connectionDirection: 'All',
                    limit: this.pageSize,
                    offset: 0,
                    orderBy: 'DateCreatedDESC',
                },
                ancillaryFilter: {
                    connectionDirection: 'All',
                    limit: this.pageSize,
                    offset: 0,
                    orderBy: 'DateCreatedDESC',
                },
                totalCount: 0,
                isLoading: false,
                search: '',
                activeTab: 'physicians',
                viewType: 'grid',
            }
        },
        computed: {
            normalizedSearchTerm() {
                return (this.search || '').trim().toLowerCase()
            },
            filteredPhysicianConnections() {
                return this.filterConnections(this.physicianConnections, 'physician')
            },
            filteredAncillaryConnections() {
                return this.filterConnections(this.ancillaryConnections, 'ancillary')
            },
            tabs() {
                const tabs = [
                    {
                        name: 'physicians',
                        displayName: this.$t('pages.network.connections.tabs.physicians'),
                        icon: 'member-transparent',
                    },
                ]

                if (!this.$auth.isPracticeAncillary()) {
                    tabs.push({
                        name: 'providers',
                        displayName: this.$t('pages.network.connections.tabs.providers'),
                        icon: 'colleague-transparent',
                    })
                }

                return tabs.map((tab) => ({
                    ...tab,
                    buttonClass: {
                        'toggle-btn': true,
                        active: this.activeTab === tab.name,
                    },
                }))
            },
            groupTabs() {
                const groupTabs = [
                    {
                        name: 'grid',
                        icon: 'grid',
                        class: 'toggle-icon-left',
                    },
                ]

                if (!this.$auth.isPracticeAncillary()) {
                    groupTabs.push({
                        name: 'list',
                        icon: 'list',
                        class: 'toggle-icon-right',
                    })
                }

                return groupTabs.map((tab) => ({
                    ...tab,
                    iconClass: {
                        'header-search-layout': true,
                        [tab.class]: true,
                        active: this.viewType === tab.name,
                    },
                }))
            },
        },

        methods: {
            filterConnections(connections, type) {
                const term = this.normalizedSearchTerm
                const validConnections = (connections || []).filter((conn) =>
                    type === 'physician' ? !!conn?.user : !!conn?.practice,
                )
                if (!term) return validConnections

                return validConnections.filter((conn) => {
                    const user = conn?.user || {}
                    const practice = conn?.practice || {}
                    const valuesToSearch = [
                        user.firstName,
                        user.lastName,
                        user.npi,
                        user.email,
                        practice.name,
                        practice.city,
                        practice.state,
                        practice.zip,
                    ]

                    return valuesToSearch.some((value) =>
                        String(value || '')
                            .toLowerCase()
                            .includes(term),
                    )
                })
            },
            tabChange(tabName) {
                this.activeTab = tabName
                if (tabName === this.tabs[0].name) {
                    this.physiciansTabClicked()
                } else {
                    this.ancillaryTabClicked()
                }
            },
            groupTabChange(groupTabName) {
                this.viewType = groupTabName
            },
            physiciansTabClicked() {
                this.isLoading = true

                this.showTabPhysicians = true
                this.physicianFilter.offset = 0
                this.$router.push({
                    ...this.$route,
                    query: { ...this.$route.query, status: 'all', practiceType: PracticeTypeEnum.PHYSICIAN },
                })
                this.fetchData().then(() => this.$refs.physicianConnectionList.resetConnectionListFilter())
            },
            ancillaryTabClicked() {
                this.isLoading = true

                this.showTabPhysicians = false
                this.ancillaryFilter.offset = 0
                this.$router.push({
                    ...this.$route,
                    query: { ...this.$route.query, status: 'all', practiceType: PracticeTypeEnum.ANCILLARY },
                })
                this.fetchData().then(() => {
                    this.$refs.ancillaryConnectionList.resetConnectionListFilter()
                })
            },
            physicianFilterChanged(filter) {
                this.isLoading = true
                this.physicianFilter.connectionDirection = filter.connectionDirection
                this.physicianFilter.orderBy = filter.orderBy

                this.physicianFilter.offset = 0

                this.fetchData(false)
            },
            ancillaryFilterChanged(filter) {
                this.isLoading = true
                this.ancillaryFilter.connectionDirection = filter.connectionDirection
                this.ancillaryFilter.orderBy = filter.orderBy

                this.ancillaryFilter.offset = 0

                this.fetchData(false)
            },
            fetchData(append) {
                const filterString = this.showTabPhysicians ? 'physicianFilter' : 'ancillaryFilter'
                this.changeIsSendingFlag(true)

                let request = {
                    practiceType: this.showTabPhysicians ? 'physician' : 'ancillary',
                    limit: this[filterString].limit,
                    offset: this[filterString].offset,
                }

                return UsersAPI.getMyConnectionsFromProviderTable(request, this.$auth.getActiveMember().id)
                    .then((conns) => {
                        this.totalCount = parseInt(conns._headers['x-total-count'])
                        this.setFetchedData(conns, append)
                        this.changeIsSendingFlag(false)
                    })
                    .finally(() => {
                        this.isLoading = false
                    })
            },

            changeIsSendingFlag(value) {
                if (this.showTabPhysicians) {
                    if (this.$refs.physicianConnectionList) {
                        this.$refs.physicianConnectionList.isSending = value
                    }
                } else {
                    if (this.$refs.ancillaryConnectionList) {
                        this.$refs.ancillaryConnectionList.isSending = value
                    }
                }
            },

            setFetchedData(conns, append) {
                if (append) {
                    let collection = this.showTabPhysicians ? this.physicianConnections : this.ancillaryConnections
                    for (let i = 0; i < conns.length; ++i) {
                        collection.push(conns[i])
                    }
                } else {
                    if (this.showTabPhysicians) this.physicianConnections = conns
                    else this.ancillaryConnections = conns
                }
            },
            physicianLoadNextPage() {
                this.physicianFilter.offset = this.physicianFilter.offset + this.physicianFilter.limit
                this.fetchData(true)
            },
            ancillaryLoadNextPage() {
                this.ancillaryFilter.offset = this.ancillaryFilter.offset + this.ancillaryFilter.limit
                this.fetchData(true)
            },
        },

        beforeCreate() {
            this.pageSize = 6
        },

        created() {
            this.isLoading = true
            this.fetchData()
        },
    }
</script>
<style scoped>
    .my-connections {
        width: 100%;
        max-width: 100%;
        min-width: 0;
        box-sizing: border-box;
    }

    @media (max-width: 1024px) {
        .my-connections ::v-deep .tabs-container {
            width: 100%;
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
            box-sizing: border-box;
        }

        .my-connections ::v-deep .tabs-container .grid {
            margin-left: 0 !important;
            margin-right: 0 !important;
            margin-bottom: 0 !important;
            transform: none !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box;
        }

        .my-connections ::v-deep .tabs-container .grid__column {
            flex-basis: 100% !important;
            max-width: 100% !important;
            min-width: 0 !important;
            width: 100% !important;
            box-sizing: border-box;
        }

        .my-connections ::v-deep .tabs-container .card,
        .my-connections ::v-deep .tabs-container .card-layout {
            width: 100% !important;
            max-width: 100% !important;
            min-width: 0;
            box-sizing: border-box;
        }
    }

    @media (max-width: 767px) {
        .my-connections ::v-deep .tabs-container .grid__column {
            padding-left: 0 !important;
            padding-right: 0 !important;
        }

        .connections-header {
            width: 100%;
            max-width: 100%;
            margin-left: 0;
            margin-right: 0;
            padding-left: 0;
            padding-right: 0;
            box-sizing: border-box;
        }

        .search-network {
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
        }
    }

    .connections-header {
        margin-bottom: 20px;
    }
    .search-network-grid {
        display: flex;
        gap: 0px;
        background-color: transparent;
        border-radius: 10px;
        width: auto;
        height: 35px;
        padding: 0px;
        align-items: center;
        border: 1px solid #c1c1c5;
        justify-content: space-between;
    }
    .search-network {
        gap: 12px;
        margin-bottom: 20px;
    }
    .toggle-icon {
        cursor: pointer;
        opacity: 0.5;
        transition: 0.2s;
    }
    i.header-search-layout.toggle-icon-right.icon.icon--size-large {
        padding: 8px;
        border-top-right-radius: 8px;
        height: 35px;
        min-width: 33px;
        border-bottom-right-radius: 8px;
    }
    i.header-search-layout.toggle-icon-left.icon.icon--size-large {
        padding: 8px;
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
        height: 35px;
        min-width: 33px;
    }
    i.header-search-layout.toggle-icon-left.icon.icon--size-large.active {
        opacity: 1;
        background-color: black;
        color: #fff;
    }
    i.header-search-layout.toggle-icon-right.icon.icon--size-large.active {
        opacity: 1;
        background-color: black;
        color: #fff;
    }
    .toggle-tabs {
        display: inline-flex;
        background: oklch(0.91 0.01 131.22);
        padding: 3px;
        border-radius: 99px;
        width: 430px;
    }
    .toggle-btn {
        border: none;
        background: transparent;
        padding: 8px 18px;
        border-radius: 999px;
        font-size: 14px;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        color: oklch(0.145 0 0);
        width: 210px;
        justify-content: center;
        font-weight: 500;
    }
    .toggle-btn.active {
        background: #fff;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
        color: oklch(0.145 0 0);
        width: 210px;
        justify-content: center;
    }
    .dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
    }
    .search {
        background-color: #f3f3f5;
        height: 36px;
        border-radius: 6.75px !important;
    }
    .bg i {
        top: 9px;
    }
    .toggle-tabs {
        width: 426px !important;
    }
    .dot.green {
        background: #22c55e;
    }
    .dot.blue {
        background: #3b82f6;
    }

    ::v-deep .tabs-container .card {
        min-height: 280px;
    }

    ::v-deep .tabs-container .card .main-name-row h3,
    ::v-deep .tabs-container .card .name-row h3 {
        line-height: 1.25;
        min-height: 36px;
    }

    @media (max-width: 719px) {
        ::v-deep .tabs-container .card {
            min-height: auto;
        }

        ::v-deep .tabs-container .card .main-name-row h3,
        ::v-deep .tabs-container .card .name-row h3 {
            min-height: 0;
        }

        .toggle-tabs {
            width: 100% !important;
        }
        .toggle-btn {
            font-size: 12px !important;
        }
        .search-network-grid {
            display: none;
        }
    }
    @media (max-width: 320px) {
        .search-network-grid {
            display: none;
        }
    }
</style>
